'use client';

import { ImagePlus, Plus, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { UserMaterial } from '@/lib/api/materials';

const focusClass = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9] focus-visible:ring-offset-2';

export function KagemaneMaterials({ materials, maxMaterials, disabled, onAdd, onRemove, optionalPeople = false }: {
  materials: UserMaterial[];
  maxMaterials: number;
  disabled: boolean;
  onAdd: () => void;
  onRemove: (id: string) => void;
  optionalPeople?: boolean;
}) {
  const t = useTranslations('kagemane');
  const atLimit = materials.length >= maxMaterials;
  const addLabel = atLimit ? t('materialLimit', { count: maxMaterials }) : t('addMaterial');

  return <div role="group" aria-label={t(optionalPeople ? 'genjutsuOptionalPeople' : 'materials')} className="rounded-2xl border border-[#E8ECEF] bg-[#F4F6F7]">
    <span aria-live="polite" aria-atomic="true" className="sr-only">{t('selectedImages', { count: materials.length })}</span>
      {materials.length === 0 ? <button type="button" disabled={disabled} onClick={onAdd}
        className={`flex min-h-32 w-full flex-col items-center justify-center gap-3 rounded-2xl p-4 transition-colors hover:bg-[#EBF8FD] disabled:opacity-50 ${focusClass}`}>
        <ImagePlus className="size-6 text-[#667680]" aria-hidden />
        <span className="text-sm font-medium text-[#53636D]">{t(optionalPeople ? 'genjutsuOptionalPeople' : 'materialDropTitle')}</span>
      </button> : <ul aria-label={t('selectedImages', { count: materials.length })}
        className="flex flex-wrap items-center gap-2 p-3">
        <li className="size-16 shrink-0">
          <button type="button" disabled={disabled || atLimit} onClick={onAdd} aria-label={addLabel} title={addLabel}
            className={`grid aspect-square min-h-11 w-full place-items-center rounded-xl border border-[#E2E7EB] bg-[#E9EDF0] text-[#53636D] transition-colors hover:border-[#4ABDE9] hover:bg-[#EBF8FD] disabled:cursor-not-allowed disabled:opacity-50 ${focusClass}`}>
            <Plus className="size-5" aria-hidden />
          </button>
        </li>
        {materials.map((material) => <li key={material.id} className="relative size-16 shrink-0">
          <img src={material.thumbnail_url || material.url} alt={material.display_name || material.name}
            className="aspect-square w-full rounded-xl bg-[#E9EEF1] object-cover" />
          <button type="button" disabled={disabled} onClick={() => onRemove(material.id)}
            aria-label={t('removeMaterial', { name: material.display_name || material.name })}
            className={`absolute -right-1 -top-1 grid size-8 place-items-center rounded-full text-[#53636D] after:absolute after:-inset-1.5 disabled:opacity-50 ${focusClass}`}>
            <span className="grid size-6 place-items-center rounded-full border border-[#E9EEF1] bg-white shadow-sm transition-colors hover:bg-[#E9EEF1]"><X className="size-3.5" aria-hidden /></span>
          </button>
        </li>)}
      </ul>}
  </div>;
}
